/**
 * Paper Terminal Settings Form Models & Options Builders.
 *
 * Formats default quantity dropdowns, settings modal view models,
 * and parses settings form updates for capital, loss limits, offsets, and themes.
 */

export interface DropdownOption {
  value: number;
  text: string;
}

/**
 * Builds the array of lot size dropdown options (1 to maxLots).
 */
export function buildSettingsQtyOptions(
  lotSize: number = 1,
  maxLots: number = 300,
): DropdownOption[] {
  const sz = Math.max(1, lotSize);
  const options: DropdownOption[] = [];
  for (let lots = 1; lots <= maxLots; lots++) {
    options.push({
      value: lots,
      text: `${lots} Lot${lots > 1 ? "s" : ""} (${lots * sz})`,
    });
  }
  return options;
}

export interface PaperSettingsViewModel {
  capital: number | string;
  lossLimit: number | string;
  profitTarget: number | string;
  ceOffset: string;
  peOffset: string;
  defaultLots: number;
  theme: string;
}

/**
 * Prepares the view model data for the Paper Terminal settings modal.
 */
export function buildPaperSettingsViewModel(
  state: {
    startCapital?: number;
    mtmLossLimit?: number | null;
    mtmTarget?: number | null;
    ceStrikeOffset?: number;
    peStrikeOffset?: number;
    defaultQtyLots?: number;
  },
  savedTheme: string = "dark",
): PaperSettingsViewModel {
  return {
    capital: state.startCapital ?? "",
    lossLimit: state.mtmLossLimit != null ? state.mtmLossLimit : "",
    profitTarget: state.mtmTarget != null ? state.mtmTarget : "",
    ceOffset: String(state.ceStrikeOffset || 0),
    peOffset: String(state.peStrikeOffset || 0),
    defaultLots: Math.max(1, Math.min(300, state.defaultQtyLots || 1)),
    theme: savedTheme || "dark",
  };
}

export interface PaperSettingsFormData {
  capital?: string | number | null;
  lossLimit?: string | number | null;
  profitTarget?: string | number | null;
  ceOffset?: string | number | null;
  peOffset?: string | number | null;
  defaultQty?: string | number | null;
  theme?: string | null;
}

export interface ParsedPaperSettings {
  capital?: number;
  mtmLossLimit: number | null;
  mtmTarget: number | null;
  ceStrikeOffset: number;
  peStrikeOffset: number;
  defaultQtyLots: number;
  theme: string;
}

/**
 * Parses user input from settings modal form fields into typed state properties.
 */
export function parsePaperSettingsFormData(
  formData: PaperSettingsFormData,
): ParsedPaperSettings {
  const capitalVal = parseFloat(String(formData.capital || ""));
  const lossVal = parseFloat(String(formData.lossLimit || ""));
  const targetVal = parseFloat(String(formData.profitTarget || ""));
  const newCeOffset = parseInt(String(formData.ceOffset || "0"), 10) || 0;
  const newPeOffset = parseInt(String(formData.peOffset || "0"), 10) || 0;
  const qtyLots = parseInt(String(formData.defaultQty || "1"), 10);

  const result: ParsedPaperSettings = {
    mtmLossLimit: !isNaN(lossVal) && lossVal > 0 ? lossVal : null,
    mtmTarget: !isNaN(targetVal) && targetVal > 0 ? targetVal : null,
    ceStrikeOffset: newCeOffset,
    peStrikeOffset: newPeOffset,
    defaultQtyLots: qtyLots > 0 ? qtyLots : 1,
    theme: String(formData.theme || "dark"),
  };

  if (!isNaN(capitalVal) && capitalVal > 0) {
    result.capital = capitalVal;
  }

  return result;
}
